import { Form, Input, Checkbox } from "antd";
import { AppButton } from "components/button/AppButton";
import googleLogo from "assets/images/googleIcon.png";
import {
  passwordValidationRules,
  signupemailValidationRules,
  textnameInputValidationRules,
} from "utils/formHelpers/validations";
import { routePath } from "config/routeMgt/routePath";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { useMutation } from "react-query";
import { createUser } from "../hooks/useCreateUser";
import { ICreateUserProps } from "../types";
import { openNotification } from "utils/notification";
import { useSignIn } from "react-auth-kit";
import { END_POINT } from "config/environment";
import { BeatLoader } from "react-spinners";
import {
  trackMetaSignupComplete,
  trackMetaSignupOAuthClick,
} from "../../../utils/metaPixelJob";
import {
  appendCampaignQuery,
  clearCampaignAttribution,
  getCampaignAttributionPayload,
} from "../../../utils/campaignAttribution";
import { Turnstile } from "components/Turnstile";
import { useTurnstileEnabled } from "../../../hooks/useFeatureFlag";
import { verifyCaptcha } from "services/captchaService";

interface MyComponentProps {
  selectedOptionProp: string;
  setStepProp: React.Dispatch<React.SetStateAction<boolean>>;
}

export const SignUpForm: React.FC<MyComponentProps> = ({
  selectedOptionProp,
  setStepProp,
}) => {
  const { mutate, isLoading } = useMutation(createUser);
  const [form] = Form.useForm();
  const signIn = useSignIn();
  const isTurnstileEnabled = useTurnstileEnabled();

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [isTurnstileValid, setIsTurnstileValid] = useState(false);
  const [isTurnstileLoaded, setIsTurnstileLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [allFieldsFilled, setAllFieldsFilled] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
  const onVerifyCalled = useRef(false);
  const verificationNoticeShown = useRef(false);


  useEffect(() => {
    if (isTurnstileEnabled) {
      if (!window.turnstile) {
        const script = document.createElement("script");
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
        script.async = true;
        script.defer = true;
        script.onload = () => {
          setIsTurnstileLoaded(true);
        };
        script.onerror = () => {
          setIsTurnstileLoaded(false);
        };
        document.head.appendChild(script);
      } else {
        setIsTurnstileLoaded(true);
      }
    }
  }, [isTurnstileEnabled]);

  useEffect(() => {
    if (isTurnstileLoaded && showCaptcha) {
      setTimeout(() => {
        const widget = document.querySelector(".cf-turnstile");
        if (!widget) {
          console.warn("⚠️ Turnstile widget not found in DOM!");
        }
      }, 500);
    }
  }, [isTurnstileLoaded, showCaptcha]);

  useEffect(() => {
    form.setFieldsValue({
      role: selectedOptionProp,
    });
  }, [selectedOptionProp, form]);

  const is_corporate = selectedOptionProp === "is_corporate" ? 1 : 0;
  const userType = (selectedOptionProp || "").toLowerCase();

  const verifyCaptchaWithBackend = async (token: string): Promise<boolean> => {
    try {
      setIsVerifying(true);
      const response = await verifyCaptcha(token);

      if (response.success === true) {
        setCaptchaVerified(true);
        openNotification({
          title: "Verification Successful",
          state: "success",
          description: "CAPTCHA verified successfully. You can now sign up.",
          duration: 3.0,
        });
        return true;
      } else {
        const errorMsg =
          response.message || response.error || "CAPTCHA verification failed.";
        setCaptchaVerified(false);
        setTurnstileToken(null);
        setIsTurnstileValid(false);

        openNotification({
          title: "Verification Failed",
          state: "error",
          description: errorMsg,
          duration: 4.5,
        });
        return false;
      }
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to verify CAPTCHA. Please try again.";
      setCaptchaVerified(false);
      setTurnstileToken(null);
      setIsTurnstileValid(false);

      openNotification({
        title: "Verification Error",
        state: "error",
        description: errorMsg,
        duration: 4.5,
      });
      return false;
    } finally {
      setIsVerifying(false);
    }
  };

  const handleTurnstileVerify = async (token: string) => {
    onVerifyCalled.current = true;
    setTurnstileToken(token);
    setIsTurnstileValid(true);

    if (token) {
      const isValid = await verifyCaptchaWithBackend(token);
      if (!isValid) {
        setTurnstileToken(null);
        setIsTurnstileValid(false);
        setCaptchaVerified(false);
      }
    }
  };

  const handleTurnstileError = (error: string) => {
    setTurnstileToken(null);
    setIsTurnstileValid(false);
    setCaptchaVerified(false);

    openNotification({
      title: "Verification Error",
      state: "error",
      description: "CAPTCHA verification failed. Please try again.",
      duration: 4.5,
    });
  };

  const handleTurnstileExpire = () => {
    setTurnstileToken(null);
    setIsTurnstileValid(false);
    setCaptchaVerified(false);

    openNotification({
      title: "Verification Expired",
      state: "info",
      description: "CAPTCHA expired. Please complete it again.",
      duration: 4.5,
    });
  };

  const handleSubmit = (data: any) => {
    if (isTurnstileEnabled && showCaptcha) {
      if (!captchaVerified) {
        openNotification({
          title: "Verification Required",
          state: "error",
          description: "Please complete the CAPTCHA verification successfully.",
          duration: 4.5,
        });
        return;
      }
    }

    setIsSubmitting(true);

    const isTalent = selectedOptionProp === "talent";

    const props: ICreateUserProps = {
      email: data.email,
      password: data.password,
      type: userType,
      name: data.name,
      password_confirmation: data.password_confirmation,
      is_corporate,
      turnstile_token:
        isTurnstileEnabled && showCaptcha && captchaVerified
          ? turnstileToken || undefined
          : undefined,
      ...getCampaignAttributionPayload(),
    };

    mutate(props, {
      onError: (err: any) => {
        const error = err.response?.data?.data;
        setIsSubmitting(false);

        if (
          err.response?.status === 400 &&
          (err.response?.data?.err === 110200 ||
            err.response?.data?.message?.includes("captcha") ||
            err.response?.data?.message?.includes("turnstile"))
        ) {
          setTurnstileToken(null);
          setIsTurnstileValid(false);
          setCaptchaVerified(false);

          openNotification({
            title: "CAPTCHA Error",
            state: "error",
            description:
              err.response?.data?.message ||
              "CAPTCHA verification failed. Please try again.",
            duration: 8.0,
          });
          return;
        }

        openNotification({
          title: "Error",
          state: "error",
          description: `${error?.email ? error.email : "An error occurred during sign up."}`,
          duration: 8.0,
        });
      },
      onSuccess: async (res: any) => {
        clearCampaignAttribution();
        if (isTalent) {
          navigate(routePath.uploadResume);
        }

        if (
          signIn({
            token: res.data.token,
            tokenType: "Bearer",
            authState: {
              ...res.data,
              userType: selectedOptionProp,
            },
            expiresIn: 180,
          })
        ) {
          trackMetaSignupComplete({
            content_name: selectedOptionProp || userType,
          });
          openNotification({
            title: "Success",
            state: "success",
            description: "Account created successfully",
            duration: 4.5,
          });
          setTurnstileToken(null);
          setIsTurnstileValid(false);
          setCaptchaVerified(false);
          setShowCaptcha(false);
        }
        setIsSubmitting(false);
      },
    });
  };

  const redirect = (platform: string) => {
    setLoading(true);
    const authPath =
      platform === "google" ? "auth" : "auth/linkedin";
    const baseUrl = `${END_POINT.AUTH_URL}/${authPath}?type=${userType}&is_corporate=${is_corporate}`;
    const url = appendCampaignQuery(baseUrl);
    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        trackMetaSignupOAuthClick(
          platform === "google" ? "google" : "linkedin",
          userType,
        );
        window.location.assign(data.url);
      })
      .catch(() => setLoading(false));
  };

  const checkFormCompletion = () => {
    const values = form.getFieldsValue();
    const { name, email, password, password_confirmation, terms } = values;

    const isFilled =
      name &&
      name.trim() !== "" &&
      email &&
      email.trim() !== "" &&
      password &&
      password.trim() !== "" &&
      password_confirmation &&
      password_confirmation.trim() !== "" &&
      password === password_confirmation &&
      terms === true;

    setAllFieldsFilled(!!isFilled);

    if (
      isFilled &&
      isTurnstileEnabled &&
      isTurnstileLoaded &&
      !showCaptcha &&
      !verificationNoticeShown.current
    ) {
      verificationNoticeShown.current = true;
      setShowCaptcha(true);
      setTurnstileToken(null);
      setIsTurnstileValid(false);
      setCaptchaVerified(false);
      onVerifyCalled.current = false;
      openNotification({
        title: "Verification Required",
        state: "info",
        description: "Please complete the CAPTCHA verification to continue.",
        duration: 4.5,
      });
    }
  };

  useEffect(() => {
    checkFormCompletion();
  }, [form]);

  const isRecruiter = userType === "recruiter";

  return (
    <>
      <div className="mb-8">
        <h2 className="text-3xl font-semibold text-gray-900 mb-2">
          Create your account
        </h2>
        <p className="text-gray-500 text-base">
          {isRecruiter
            ? "Start finding top talent for your team today."
            : "Sign up to find your next role."}
        </p>
      </div>

      <Form
        layout="vertical"
        form={form}
        requiredMark={false}
        onFinish={handleSubmit}
        onFinishFailed={() => {}}
        onFieldsChange={() => {
          checkFormCompletion();
        }}
      >
        <Form.Item noStyle name="role">
          <Input type="hidden" />
        </Form.Item>

        <Form.Item
          name="name"
          label={
            <span>
              Name <span style={{ color: "red" }}>*</span>
            </span>
          }
          rules={textnameInputValidationRules(userType, !!is_corporate)}
        >
          <Input
            placeholder="Enter your name"
            className="rounded-xl py-3 px-4 border border-gray-300"
          />
        </Form.Item>

        <Form.Item
          name="email"
          label={
            <span>
              Email <span style={{ color: "red" }}>*</span>
            </span>
          }
          rules={signupemailValidationRules(!!is_corporate)}
        >
          <Input
            placeholder="Enter your email"
            className="rounded-xl py-3 px-4 border border-gray-300"
          />
        </Form.Item>

        <Form.Item
          name="password"
          label={
            <span>
              Password <span style={{ color: "red" }}>*</span>
            </span>
          }
          rules={passwordValidationRules}
        >
          <Input.Password
            placeholder="Create a password"
            className="rounded-xl py-3 px-4 border border-gray-300"
          />
        </Form.Item>

        <Form.Item
          name="password_confirmation"
          label={
            <span>
              Confirm Password <span style={{ color: "red" }}>*</span>
            </span>
          }
          dependencies={["password"]}
          rules={[
            { required: true, message: "Please confirm your password" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject("Passwords do not match");
              },
            }),
          ]}
        >
          <Input.Password
            placeholder="Confirm your password"
            className="rounded-xl py-3 px-4 border border-gray-300"
          />
        </Form.Item>

        <Form.Item
          name="terms"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject("You must accept the Terms & Conditions"),
            },
          ]}
        >
          <Checkbox>
            I agree to{" "}
            <a href="https://www.arnopro.com/arno-terms-of-use" target="_blank">
              Terms & Conditions
            </a>{" "}
            and{" "}
            <a
              href="https://www.arnopro.com/arno-data-privacy-policy"
              target="_blank"
            >
              Privacy Policy
            </a>
          </Checkbox>
        </Form.Item>

        {isTurnstileEnabled &&
          isTurnstileLoaded &&
          showCaptcha &&
          !captchaVerified && (
            <Form.Item
              label="Verification"
              required
              validateStatus={
                turnstileToken !== null && !captchaVerified ? "error" : ""
              }
              help={
                turnstileToken !== null && !captchaVerified
                  ? "CAPTCHA verification failed. Please try again."
                  : "Please complete the CAPTCHA verification to continue."
              }
            >
              <Turnstile
                sitekey={TURNSTILE_SITE_KEY}
                onVerify={handleTurnstileVerify}
                onError={handleTurnstileError}
                onExpire={handleTurnstileExpire}
                theme="auto"
                size="normal"
              />
            </Form.Item>
          )}

        {isTurnstileEnabled && captchaVerified && showCaptcha && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2">
            <svg
              className="w-5 h-5 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="text-sm text-green-700 font-medium">
              ✓ CAPTCHA verified successfully!
            </span>
          </div>
        )}

        {isTurnstileEnabled && !isTurnstileLoaded && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-700 flex items-center gap-2">
              <i className="ri-error-warning-line text-yellow-600"></i>
              Loading CAPTCHA verification...
            </p>
          </div>
        )}

        <AppButton
          label={isVerifying ? "Verifying..." : "Sign Up"}
          type="submit"
          additionalClassNames={[
            "w-full py-3.5 rounded-xl font-medium",
            captchaVerified && isTurnstileEnabled && showCaptcha
              ? "bg-green-600 hover:bg-green-700 text-white"
              : "bg-primary text-white",
          ]}
          isLoading={isLoading || isSubmitting || isVerifying}
          disabled={
            isLoading ||
            isSubmitting ||
            isVerifying ||
            (isTurnstileEnabled && showCaptcha && !captchaVerified)
          }
        />
      </Form>

      <button
        onClick={() => redirect("google")}
        className="w-full border border-gray-300 py-3 rounded-xl flex items-center justify-center gap-3 hover:bg-gray-50 mt-4"
      >
        <img src={googleLogo} className="w-5 h-5" />
        <span>{loading ? <BeatLoader size={8} /> : "Sign up with Google"}</span>
      </button>
    </>
  );
};;
